<template>
  <div>
    <loadingCube v-if="loading"/>

    <div v-else>
      <table class="ns-table">
        <thead>
        <tr>
          <th :style="zIndexReverse(totalCols, 0)">
            <div class="capsule"><span>Created</span></div>
          </th>

          <th :style="zIndexReverse(totalCols, isWindowed ? 2 : 1)">
            <div class="capsule"><span>Type</span></div>
          </th>

          <th v-if="isWindowed" class="rotate" :style="zIndexReverse(totalCols, 1)">
            <div class="capsule"><span>Window</span></div>
          </th>

          <th class="rotate" :style="zIndexReverse(totalCols, isWindowed ? 3 : 2)">
            <div class="capsule"><span>Company</span></div>
          </th>

          <th class="rotate" :style="zIndexReverse(totalCols, isWindowed ? 4 : 3)">
            <div class="capsule"><span>Contact</span></div>
          </th>

          <th class="rotate" :style="zIndexReverse(totalCols, isWindowed ? 5 : 4)">
            <div class="capsule"><span>Pipeline status</span></div>
          </th>

          <th class="rotate" :style="zIndexReverse(totalCols, isWindowed ? 5 : 4)">
            <div class="capsule"><span>Call type</span></div>
          </th>

          <th class="rotate" :style="zIndexReverse(totalCols, isWindowed ? 6 : 5)">
            <div class="capsule"><span>Decision&nbsp;maker</span></div>
          </th>

          <th class="rotate" :style="zIndexReverse(totalCols, isWindowed ? 7 : 6)">
            <div class="capsule"><span>Description</span></div>
          </th>
        </tr>
        </thead>

        <tbody>
        <tr v-for="r in rows" :key="r.note_id + '-' + (r.audit_log_id || 0)">
          <td>
            <NotesQuickLink
                mode="notes-user"
                :query="{...query}"
                :params="{...params, start: startOfDay(r.created_at), end: endOfDay(r.created_at)}"
            >
              {{ formatCreated(r.created_at) }}
            </NotesQuickLink>
            <NotesQuickLink
                mode="notes-global"
                :query="{...query}"
                :params="{...params, start: startOfDay(r.created_at), end: endOfDay(r.created_at)}"
            >
              (g)
            </NotesQuickLink>
          </td>

          <td v-if="isWindowed">{{ r.time_window }}</td>

          <td>
            <div class="d-flex flex-column">
              <strong>
                <NotesQuickLink
                    mode="notes-user"
                    :query="{...query, match: patternFromIdents(r.note_ident) }"
                    :params="params"
                >
                  {{ noteTypeLabels[r.note_ident] || r.note_ident }}
                </NotesQuickLink>
              </strong>
              <small class="text-muted">{{ r.note_type_name }}</small>
              <small v-if="r.is_call" class="text-muted">
                vm={{ r.voicemail }} · tax_disc={{ r.discussed_tax }} · tax_sold={{ r.sold_tax }}
              </small>
            </div>
          </td>

          <td>
            <router-link v-if="r.company_name" :to="{name: 'company_record', query: {company_id: r.company_id}}" target="_blank">
              {{ r.company_name || '—' }}
            </router-link>
            <div v-else>-</div>
          </td>

          <td>
            <template v-if="r.belongs_to_type && r.belongs_to_type.includes('Companies\\Contacts\\Contact')">
              <router-link :to="{name: 'contact_profile', query: {contact_id: r.belongs_to_id}}" target="_blank">
                {{ r.contact_name || '-' }}
              </router-link>
            </template>
            <template v-else>-</template>
          </td>

          <td>{{ r.pipeline_value }}</td>
          <td>{{ callTypeLabel(r) }}</td>
          <td>{{ decisionMakerLabel(r) }}</td>
          <td><span :title="r.description || ''">{{ truncate(r.description || '', 140) }}</span></td>
        </tr>

        <tr v-if="!rows.length">
          <td :colspan="isWindowed ? 8 : 7" class="text-center text-muted">No rows</td>
        </tr>
        </tbody>
      </table>

      <div class="d-flex align-items-center justify-content-between mt-2">
        <div class="text-muted">
          Page {{ page }} / {{ lastPage }} · {{ total }} total · per page {{ effectiveLimit }}
        </div>
        <nav aria-label="Pagination">
          <ul class="pagination pagination-sm mb-0">
            <li :class="{ disabled: page <= 1 }" class="page-item">
              <a class="page-link" href="#" @click.prevent="goTo(1)">&laquo;</a>
            </li>
            <li :class="{ disabled: page <= 1 }" class="page-item">
              <a class="page-link" href="#" @click.prevent="goTo(page - 1)">&lsaquo;</a>
            </li>

            <li v-for="p in pagesAround" :key="p" :class="{ active: p === page, disabled: p === '…' }" class="page-item">
              <a v-if="p !== '…'" class="page-link" href="#" @click.prevent="goTo(p)">{{ p }}</a>
              <span v-else class="page-link">…</span>
            </li>

            <li :class="{ disabled: page >= lastPage }" class="page-item">
              <a class="page-link" href="#" @click.prevent="goTo(page + 1)">&rsaquo;</a>
            </li>
            <li :class="{ disabled: page >= lastPage }" class="page-item">
              <a class="page-link" href="#" @click.prevent="goTo(lastPage)">&raquo;</a>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </div>
</template>

<script>
import loadingCube from 'loaders/record_loader';
import NotesQuickLink from "../shared/notes_link.vue";
import {
  formatAtDayBoundary,
  startOfDay,
  endOfDay,
  formatCreated,
  patternFromIdents, zIndexReverse
} from '../shared/utils'


export default {
  name: 'sunset_note_stats_user_raw',
  components: {NotesQuickLink, loadingCube },
  props: {
    // from <sunset-note-stats-query-bar v-slot="{ params, meta, query }">
    params: { type: Object, required: true }, // { userId, side, start, end, windowed, mask, match, notMatch, includeOther, limit, apiBaseUrl, ... }
    meta:   { type: Object, required: true },
    query:  { type: Object, default: () => ({}) },
  },
  data() {
    return {
      loading: true,
      rows: [],
      page: 1,
      total: 0,
      lastPage: 1,
      pageSize: null,
      refreshTimer: null,
      defaultApiBaseUrl: '/api/v1/',
    };
  },
  computed: {
    apiBase() {
      const base = this.params?.apiBaseUrl || this.defaultApiBaseUrl;
      return String(base).replace(/\/+$/, '');
    },

    // merge params + query (query wins) + normalize
    effectiveQuery() {
      const p = this.params || {};
      const q = this.query  || {};
      const m = { ...p, ...q };

      const user = (m.user ?? m.userId ?? m.user_id);
      const limit = m.limit != null ? Number(m.limit) : 200;

      return {
        user: (user === '' || user == null) ? null : user,
        side:  String(m.side || 'combined').toLowerCase(),
        mask:  String(m.mask || 'all'),
        start: m.start || '',
        end:   m.end   || '',
        match: m.match ?? '',
        not:   (m.not ?? m.notMatch ?? ''),
        includeOther: this.parseBool(m.includeOther ?? true, true),
        windowed:     this.parseBool(m.windowed ?? p.windowed ?? false, false),
        page:  m.page != null ? Math.max(1, parseInt(m.page, 10) || 1) : this.page,
        limit: Number.isFinite(limit) && limit > 0 ? Math.min(1000, Math.floor(limit)) : 200,
      };
    },

    isWindowed() { return this.effectiveQuery.windowed; },
    noteTypeLabels() { return this.meta?.note_types?.labels || {}; },

    effectiveLimit() {
      const fromApi = Number(this.pageSize);
      return Number.isFinite(fromApi) && fromApi > 0 ? fromApi : this.effectiveQuery.limit;
    },

    requestUrl() {
      const q = this.effectiveQuery;
      const userPath = q.user == null ? '' : `/${encodeURIComponent(q.user)}`;
      const usp = new URLSearchParams();

      usp.set('side', q.side);
      usp.set('mask', q.mask);
      if (q.start) usp.set('start', q.start);
      if (q.end)   usp.set('end', q.end);
      usp.set('includeOther', q.includeOther ? '1' : '0');
      if (q.windowed) usp.set('windowed', '1');
      if (q.match)    usp.set('match', q.match);
      if (q.not)      usp.set('not', q.not);
      usp.set('page', String(this.page));     // component source of truth for page
      usp.set('limit', String(q.limit));

      // harmless pass-throughs if backend ignores:
      if (this.query.perPage != null) usp.set('perPage', String(this.query.perPage));

      return `${this.apiBase}/sunset/reports/note-stats/notes/user${userPath}?${usp.toString()}`;
    },

    pagesAround() {
      const lp = this.lastPage || 1;
      const p = Math.min(Math.max(1, this.page), lp);
      const nums = new Set([1, lp, p - 2, p - 1, p, p + 1, p + 2].filter(x => x >= 1 && x <= lp));
      const arr = Array.from(nums).sort((a, b) => a - b);
      const out = [];
      for (let i = 0; i < arr.length; i++) {
        out.push(arr[i]);
        if (i < arr.length - 1 && arr[i + 1] !== arr[i] + 1) out.push('…');
      }
      return out;
    },

    totalCols() { return this.isWindowed ? 8 : 7; },
  },
  watch: {
    params: { handler() { this.scheduleFetchReset(); }, deep: true },
    query:  { handler() { this.scheduleFetchReset(); }, deep: true },
  },
  async mounted() {
    await this.fetchPage(1);
  },
  beforeDestroy() {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
  },
  methods: {
    patternFromIdents,
    zIndexReverse,

    parseBool(v, fallback = false) {
      if (typeof v === 'boolean') return v;
      const s = String(v ?? '').toLowerCase();
      if (['1','true','yes','on'].includes(s)) return true;
      if (['0','false','no','off'].includes(s)) return false;
      return fallback;
    },

    truncate(text, n = 120) {
      if (!text) return '';
      return text.length <= n ? text : text.slice(0, n - 1).trimEnd() + '…';
    },

    parseMaybeJSON(val) {
      if (!val) return {};
      if (typeof val === 'object') return val;
      try { return JSON.parse(String(val)); } catch { return {}; }
    },
    companyFrom(r) { return this.parseMaybeJSON(r.company_detailed); },
    contactFrom(r) { return this.parseMaybeJSON(r.contact_detailed); },

    formatAtDayBoundary,
    startOfDay,
    endOfDay,
    formatCreated,

    callTypeLabel(r) {
      if (!r || !r.is_call) return '—';
      const t = String(r.call_type || '').toUpperCase();
      if (t === 'INCOMING') return 'Incoming';
      if (t === 'OUTGOING') return 'Outgoing';
      return t || '—';
    },

    decisionMakerLabel(r) {
      if (!r || !r.is_call) return '—';
      const v = Number(r.decision_maker);
      return v === 1 ? 'Yes' : 'No';
    },

    async fetchPage(p = this.page) {
      const { start, end } = this.effectiveQuery;
      this.loading = true;
      this.page = Math.max(1, Number(p) || 1);

      try {
        if (!start || !end) {
          this.rows = [];
          this.total = 0;
          this.pageSize = null;
          this.lastPage = 1;
          return;
        }

        const { data } = await axios.get(this.requestUrl);

        // rows
        const rows = Array.isArray(data?.data) ? data.data : [];

        // meta (Laravel paginator fields are top-level)
        const total   = Number(data?.total ?? 0) || 0;
        const perPage = Number(data?.per_page ?? this.effectiveQuery.limit) || this.effectiveQuery.limit;
        const current = Number(data?.current_page ?? this.page) || this.page;
        const last    = Number(data?.last_page ?? Math.max(1, Math.ceil(total / (perPage || 1)))) || 1;

        // commit
        this.rows     = rows;
        this.total    = total;
        this.pageSize = perPage;
        this.page     = current;
        this.lastPage = last;

      } catch (e) {
        console.error(e);
        this.rows = [];
        this.total = 0;
        this.pageSize = null;
        this.lastPage = 1;
      } finally {
        this.loading = false;
      }
    },

    scheduleFetchReset() {
      if (this.refreshTimer) clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => {
        this.page = 1;
        this.fetchPage(1);
      }, 120);
    },

    goTo(p) {
      const target = Math.max(1, Math.min(this.lastPage || 1, Number(p)));
      if (target !== this.page) this.fetchPage(target);
    },
  },
};
</script>

<style lang="scss" scoped>
@use "../../scss/shared" as *;
</style>
